import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Form, Input, InputNumber, Select, Button, Switch, Modal, Row, Col, Spin, message, Tooltip } from "antd";
import { Info, Pencil, Trash2, X } from "lucide-react";
import styles from "./ProductBuilder.module.css";
import { cdnUrl, cdnUrlForStorageKey, mediaPath, basenameFromStorageKey } from "../../config/mediaPaths";
import { fetchManifestForPath, invalidateManifestFetchCacheForPath } from "../../utils/mediaManifest";
import { setGalleryManifestForPath } from "../../store/gallerySlice";
import { isHeicFile } from "../../utils/heicConversion";
import { PRODUCT_BUILDER_LABELS } from "../../labels/productBuilderLabels";
import { VARIANT_CUSTOMISATION_TEXT_MAX } from "../../config/fieldLimits";
import { GenerateShortID } from "../Hub/StoreUtils";
import LabelWithInfo from "./LabelWithInfo";

const { TextArea } = Input;

const VALID_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/webp"];
const IMAGE_UPLOAD_ACCEPT = "image/jpeg,image/png,image/webp";

const uid = () => `pb_${GenerateShortID(6)}`;

const manifestEntryHasTag = (entry, tag) =>
  Array.isArray(entry?.tags) && entry.tags.some((value) => String(value).toLowerCase() === tag);

const defaultVariantForm = () => ({ name: "", type: "single", isRequired: true, Options: [] });
const defaultCustomisationForm = () => ({
  name: "",
  type: "boolean",
  isRequired: true,
  price: "",
  addonPrice: true,
  DropdownOptions: [],
  acceptedFileTypes: [],
  maxFiles: 1,
  calculatePricePerFile: false,
});

const customisationToForm = (customisation) => ({
  name: customisation.name,
  type: customisation.type,
  isRequired: customisation.isRequired !== false,
  price: customisation.price,
  addonPrice: customisation.addonPrice,
  DropdownOptions: (customisation.DropdownOptions || []).map((option) => ({ ...option })),
  acceptedFileTypes: customisation.acceptedFileTypes || [],
  maxFiles: customisation.maxFiles ?? 1,
  calculatePricePerFile: customisation.calculatePricePerFile === true || customisation.calculatePricePerPage === true,
});

const hasVariantDraftContent = (draft) =>
  !!String(draft?.name || "").trim() ||
  draft?.type !== "single" ||
  (Array.isArray(draft?.Options) &&
    draft.Options.some((option) =>
      !!String(option?.label || "").trim() ||
      option?.price !== "" ||
      option?.addonPrice !== true
    ));

const hasCustomisationDraftContent = (draft) =>
  !!String(draft?.name || "").trim() ||
  draft?.type !== "boolean" ||
  draft?.isRequired === false ||
  (draft?.price !== "" && draft?.price !== 0) ||
  draft?.addonPrice !== true ||
  (draft?.type === "file" && (
    (Number(draft?.maxFiles) || 1) !== 1 ||
    draft?.calculatePricePerFile === true ||
    (Array.isArray(draft?.acceptedFileTypes) && draft.acceptedFileTypes.length > 0)
  )) ||
  (Array.isArray(draft?.DropdownOptions) &&
    draft.DropdownOptions.some((option) => !!String(option?.label || "").trim()));

const ProductBuilder = React.forwardRef(({ visible, initialData, onSave, onCancel, itemType = "PRODUCT", saving, token, hubId, bizId, bizPrivate = false, storeType = "", inline = false, showSaveButton = true, externalSaveSignal = 0, onDirtyChange, onDraftChange, prebookingDisabled = false }, ref) => {
  const dispatch = useDispatch();
  const isService = itemType === "SERVICE";
  const [form] = Form.useForm();
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [savedVariants, setSavedVariants] = useState([]);
  const [savedCustomisations, setSavedCustomisations] = useState([]);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [showCustForm, setShowCustForm] = useState(false);
  const [vForm, setVForm] = useState({ name: "", type: "single", isRequired: true, Options: [] });
  const [optionErrors, setOptionErrors] = useState({});
  const [cForm, setCForm] = useState(defaultCustomisationForm);
  const [editVariantIdx, setEditVariantIdx] = useState(null);
  const [editCustIdx, setEditCustIdx] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [pickSelectedKeys, setPickSelectedKeys] = useState([]);
  const [galleryEntries, setGalleryEntries] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  const pickableGalleryEntries = useMemo(() => {
    if (!Array.isArray(galleryEntries)) return [];
    const neededTag = isService ? "service" : "product";
    return galleryEntries.filter((e) => manifestEntryHasTag(e, neededTag));
  }, [galleryEntries, isService]);
  const [galleryEmpty, setGalleryEmpty] = useState(false);
  const hydratingRef = useRef(false);
  const lastExternalSaveSignalRef = useRef(externalSaveSignal);

  const markDirty = () => {
    if (!hydratingRef.current && onDirtyChange) onDirtyChange(true);
  };

  const markNestedDraftDirty = () => {
    markDirty();
  };

  const emitDraftChange = (valuesOverride = null, stateOverride = {}) => {
    if (hydratingRef.current || !onDraftChange) return;
    onDraftChange({
      ...form.getFieldsValue(),
      ...(valuesOverride || {}),
      variants: stateOverride.savedVariants ?? savedVariants,
      customisations: stateOverride.savedCustomisations ?? savedCustomisations,
      _pendingImages: stateOverride.selectedFiles ?? selectedFiles,
      _existingImages: stateOverride.existingImages ?? existingImages,
    });
  };

  useEffect(() => {
    hydratingRef.current = true;
    if (visible && initialData) {
      form.setFieldsValue({
        ...initialData,
        VideoUrls: initialData.VideoUrls ?? [],
        ItemAvailable: initialData.ItemAvailable !== false,
        RequiresPrebooking: initialData.RequiresPrebooking === true,
      });
      setSavedVariants(Array.isArray(initialData.Variants) ? initialData.Variants : []);
      setSavedCustomisations(Array.isArray(initialData.Customisations) ? initialData.Customisations : []);
      setExistingImages(Array.isArray(initialData.ItemImages) ? initialData.ItemImages : []);
      setSelectedFiles(Array.isArray(initialData._pendingImages) ? initialData._pendingImages : []);
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({ VideoUrls: [], ItemAvailable: true, RequiresPrebooking: false });
      setSavedVariants([]);
      setSavedCustomisations([]);
      setExistingImages([]);
      setSelectedFiles([]);
    }
    setEditVariantIdx(null);
    setEditCustIdx(null);
    setShowVariantForm(false);
    setShowCustForm(false);
    setVForm(defaultVariantForm());
    setCForm(defaultCustomisationForm());
    setOptionErrors({});
    queueMicrotask(() => {
      hydratingRef.current = false;
    });
  }, [visible, initialData, form]);

  useEffect(() => {
    if (!visible && onDirtyChange) {
      if (onDirtyChange) onDirtyChange(false);
    }
  }, [visible, onDirtyChange]);

  const handleSave = async () => {
    if (saving) return;
    try {
      const values = await form.validateFields();
      const nextVariants = buildVariantsForSave({ warn: true });
      if (!nextVariants) return false;
      const nextCustomisations = buildCustomisationsForSave({ warn: true });
      if (!nextCustomisations) return false;
      const savedItem = await Promise.resolve(onSave({ ...values, variants: nextVariants, customisations: nextCustomisations, _pendingImages: selectedFiles, _existingImages: existingImages }));
      const nextImages = Array.isArray(savedItem?.ItemImages) ? savedItem.ItemImages : existingImages;
      setSavedVariants(nextVariants);
      setSavedCustomisations(nextCustomisations);
      setExistingImages(nextImages);
      setSelectedFiles([]);
      setVForm(defaultVariantForm());
      setCForm(defaultCustomisationForm());
      setEditVariantIdx(null);
      setEditCustIdx(null);
      setShowVariantForm(false);
      setShowCustForm(false);
      emitDraftChange(values, { savedVariants: nextVariants, savedCustomisations: nextCustomisations, existingImages: nextImages, selectedFiles: [] });
      if (onDirtyChange) onDirtyChange(false);
      return true;
    } catch {
      /* validation errors handled by AntD */
      return false;
    }
  };

  React.useImperativeHandle(ref, () => ({
    getItemData: () => ({
      ...form.getFieldsValue(),
      variants: savedVariants,
      customisations: savedCustomisations,
      _pendingImages: selectedFiles,
      _existingImages: existingImages,
    }),
    hasUnsavedNestedDraft: () =>
      ((showVariantForm || editVariantIdx != null) && hasVariantDraftContent(vForm)) ||
      ((showCustForm || editCustIdx != null) && hasCustomisationDraftContent(cForm)),
    saveItem: handleSave,
  }));

  useEffect(() => {
    if (externalSaveSignal === lastExternalSaveSignalRef.current) return;
    lastExternalSaveSignalRef.current = externalSaveSignal;
    if (!visible || externalSaveSignal <= 0) return;
    handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSaveSignal, visible]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const slotsLeft = MAX_IMAGES - existingImages.length - selectedFiles.length;
    if (slotsLeft <= 0) {
      message.warning(`You can add at most ${MAX_IMAGES} images total.`);
      if (e.target) e.target.value = "";
      return;
    }
    const supported = files.filter((file) => {
      if (isHeicFile(file)) {
        message.error(`${file.name}: HEIC/HEIF images are not supported. Use JPG, PNG, or WebP.`);
        return false;
      }
      if (!VALID_TYPES.includes(file.type)) {
        message.warning(`${file.name}: unsupported type (JPG/PNG/WebP only)`);
        return false;
      }
      return true;
    });
    if (supported.length === 0) {
      if (e.target) e.target.value = "";
      return;
    }
    if (supported.length > slotsLeft) {
      message.warning(`You can add ${slotsLeft} more image(s).`);
    }
    setSelectedFiles(prev => {
      const next = [...prev, ...supported.slice(0, slotsLeft)].slice(0, MAX_IMAGES);
      emitDraftChange(null, { selectedFiles: next });
      return next;
    });
    markDirty();
    if (e.target) e.target.value = "";
  };

  const imageDisplayUrl = (img) => {
    if (!img?.imageKey) return null;
    const key = String(img.imageKey);
    if (!key.includes("/") && hubId && bizId) {
      return cdnUrl({ entity: "item", fileType: "media", hubId, bizId, fileName: basenameFromStorageKey(key) || key });
    }
    return cdnUrlForStorageKey(key);
  };

  const refreshGalleryManifest = async () => {
    if (!hubId || !bizId || !token) return;
    setGalleryLoading(true);
    try {
      const prefix = mediaPath({ entity: "item", fileType: "media", hubId, bizId });
      invalidateManifestFetchCacheForPath(prefix);
      const manifest = await fetchManifestForPath(prefix, { token, preferOrigin: true });
      const images = manifest?.images || [];
      setGalleryEntries(images);
      setGalleryEmpty(images.length === 0);
      if (manifest) dispatch(setGalleryManifestForPath({ pathPrefix: prefix, manifest }));
    } catch { setGalleryEntries([]); setGalleryEmpty(true); }
    setGalleryLoading(false);
  };

  useEffect(() => {
    if (visible && hubId && bizId && token) refreshGalleryManifest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, hubId, bizId, token]);

  const MAX_IMAGES = 5;

  const togglePickKey = (entry) => {
    const keyBasename = basenameFromStorageKey(entry.imageKey) || entry.imageKey;
    setPickSelectedKeys((prev) => {
      const has = prev.includes(keyBasename);
      if (has) return prev.filter((k) => k !== keyBasename);
      if (existingImages.some((img) => (basenameFromStorageKey(img.imageKey) || img.imageKey) === keyBasename)) {
        message.warning("Image already attached to this item.");
        return prev;
      }
      const remainingSlots = MAX_IMAGES - existingImages.length - selectedFiles.length - prev.length;
      if (remainingSlots <= 0) {
        message.warning(`You can add at most ${MAX_IMAGES} images total.`);
        return prev;
      }
      return [...prev, keyBasename];
    });
  };

  const applyPickSelection = () => {
    const toAdd = pickableGalleryEntries.filter((e) => pickSelectedKeys.includes(basenameFromStorageKey(e.imageKey) || e.imageKey));
    if (!toAdd.length) { message.info("No images selected."); return; }
    const next = [...existingImages];
    const remaining = MAX_IMAGES - next.length - selectedFiles.length;
    let added = 0;
    for (const entry of toAdd) {
      if (added >= remaining) break;
      const k = basenameFromStorageKey(entry.imageKey) || entry.imageKey;
      if (next.some((img) => (basenameFromStorageKey(img.imageKey) || img.imageKey) === k)) continue;
      next.push({
        imageKey: k,
        originalName: entry.originalFilename || entry.imageKey,
      });
      added += 1;
    }
    if (added === 0) { message.info("No new images to add."); return; }
    setExistingImages(next);
    emitDraftChange(null, { existingImages: next });
    markDirty();
    setGalleryOpen(false);
    setPickSelectedKeys([]);
    message.success(`${added} image(s) added from gallery.`);
  };

  function normalizeVariantDraft({ warn }) {
    if (!hasVariantDraftContent(vForm)) return null;
    if (!String(vForm.name || "").trim()) {
      if (warn) message.warning("Enter variant name");
      return false;
    }
    const errors = {};
    let hasError = false;
    vForm.Options.forEach((option, index) => {
      if (option.label && (option.price === "" || option.price === undefined || option.price === null)) {
        errors[index] = "Enter price";
        hasError = true;
      }
    });
    if (warn) setOptionErrors(errors);
    if (hasError) return false;
    return {
      ...vForm,
      name: String(vForm.name).trim(),
      Options: vForm.Options.map((option) => ({
        ...option,
        price: option.price === "" ? 0 : Number(option.price),
      })),
    };
  }

  function buildVariantsForSave({ warn }) {
    const normalized = normalizeVariantDraft({ warn });
    if (normalized === false) return false;
    if (!normalized) return savedVariants;
    const updated = [...savedVariants];
    if (editVariantIdx != null && savedVariants[editVariantIdx]) {
      updated[editVariantIdx] = { ...normalized, variantId: savedVariants[editVariantIdx].variantId };
      return updated;
    }
    return [...updated, { ...normalized, variantId: uid() }];
  }

  function normalizeCustomisationDraft({ warn }) {
    if (!hasCustomisationDraftContent(cForm)) return null;
    if (!String(cForm.name || "").trim()) {
      if (warn) message.warning("Enter customisation name");
      return false;
    }
    const normalized = {
      ...cForm,
      name: String(cForm.name).trim(),
      price: cForm.price === "" ? 0 : Number(cForm.price),
    };
    if (normalized.type === "file") {
      normalized.maxFiles = Math.min(Math.max(Number(cForm.maxFiles) || 1, 1), 5);
      normalized.calculatePricePerFile = cForm.calculatePricePerFile === true;
      normalized.acceptedFileTypes = Array.isArray(cForm.acceptedFileTypes) ? cForm.acceptedFileTypes : [];
      delete normalized.calculatePricePerPage;
    } else {
      delete normalized.maxFiles;
      delete normalized.calculatePricePerFile;
      delete normalized.calculatePricePerPage;
      if (normalized.type !== "file") delete normalized.acceptedFileTypes;
    }
    if (normalized.type !== "dropdown") {
      normalized.DropdownOptions = [];
    }
    return normalized;
  }

  const commitCustomisationForm = (index) => {
    if (!cForm.name) {
      message.warning("Enter customisation name");
      return;
    }
    const normalized = normalizeCustomisationDraft({ warn: false });
    if (!normalized || normalized === false) return;
    const updated = [...savedCustomisations];
    if (index != null && savedCustomisations[index]) {
      updated[index] = { ...normalized, customisationId: savedCustomisations[index].customisationId };
    } else {
      updated.push({ ...normalized, customisationId: uid() });
    }
    setSavedCustomisations(updated);
    emitDraftChange(null, { savedCustomisations: updated });
    markDirty();
    setCForm(defaultCustomisationForm());
    setEditCustIdx(null);
    setShowCustForm(false);
  };

  function buildCustomisationsForSave({ warn }) {
    const normalized = normalizeCustomisationDraft({ warn });
    if (normalized === false) return false;
    if (!normalized) return savedCustomisations;
    const updated = [...savedCustomisations];
    if (editCustIdx != null && savedCustomisations[editCustIdx]) {
      updated[editCustIdx] = { ...normalized, customisationId: savedCustomisations[editCustIdx].customisationId };
      return updated;
    }
    return [...updated, { ...normalized, customisationId: uid() }];
  }

  const addVariantOption = () => {
    markNestedDraftDirty();
    setVForm({ ...vForm, Options: [...vForm.Options, { optionId: uid(), label: "", price: 0, addonPrice: true, isDefault: false }] });
  };

  const updateVariantOption = (idx, field, val) => {
    markNestedDraftDirty();
    let opts = [...vForm.Options];
    if (field === "isDefault" && val === true) {
      opts = opts.map((o, i) => ({ ...o, isDefault: i === idx }));
    } else {
      opts[idx] = { ...opts[idx], [field]: val };
    }
    setVForm({ ...vForm, Options: opts });
    if (field === "price" || field === "addonPrice") {
      setOptionErrors((prev) => ({ ...prev, [idx]: "" }));
    }
  };

  const removeVariantOption = (idx) => {
    markNestedDraftDirty();
    setVForm({ ...vForm, Options: vForm.Options.filter((_, i) => i !== idx) });
  };

  const addDropdownChoice = () => {
    markNestedDraftDirty();
    setCForm({ ...cForm, DropdownOptions: [...cForm.DropdownOptions, { optionId: uid(), label: "" }] });
  };

  const updateDropdownChoice = (idx, val) => {
    markNestedDraftDirty();
    const opts = [...cForm.DropdownOptions];
    opts[idx] = { ...opts[idx], label: val };
    setCForm({ ...cForm, DropdownOptions: opts });
  };

  const removeDropdownChoice = (idx) => {
    markNestedDraftDirty();
    setCForm({ ...cForm, DropdownOptions: cForm.DropdownOptions.filter((_, i) => i !== idx) });
  };

  if (!visible && !inline) return null;

  const renderVariantHeaderFields = () => (
    <>
      <div className={styles.subFormRow}>
        <div className={styles.custFieldGrow}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Variant name</div>
          <Input
            placeholder="e.g. Size"
            className={styles.input}
            maxLength={VARIANT_CUSTOMISATION_TEXT_MAX}
            value={vForm.name}
            onChange={(e) => { markNestedDraftDirty(); setVForm({ ...vForm, name: e.target.value }); }}
          />
        </div>
        <div className={styles.custFieldRequired}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>
            <Tooltip title="If required, the customer must select an option before adding to cart">Required</Tooltip>
          </div>
          <Switch
            checked={vForm.isRequired !== false}
            onChange={(val) => { markNestedDraftDirty(); setVForm({ ...vForm, isRequired: val }); }}
          />
        </div>
      </div>
      <div className={styles.subFormRow}>
        <div className={styles.custFieldGrow}>
          <LabelWithInfo
            label={PRODUCT_BUILDER_LABELS.selectionTypeLabel}
            hint={PRODUCT_BUILDER_LABELS.selectionTypeHint}
          />
          <Select
            className={`${styles.select} ${styles.selectFull}`}
            value={vForm.type}
            popupMatchSelectWidth
            optionLabelProp="label"
            onChange={(val) => { markNestedDraftDirty(); setVForm({ ...vForm, type: val }); }}
          >
            <Select.Option value="single" label="Single choice">Single choice</Select.Option>
            <Select.Option value="multiple" label="Multiple choice">Multiple choice</Select.Option>
          </Select>
        </div>
      </div>
    </>
  );

  const renderCustomisationFields = () => (
    <>
      {/* Row 1: Name + Required */}
      <div className={styles.subFormRow}>
        <div className={styles.custFieldGrow}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Name</div>
          <Input
            placeholder="e.g. Gift wrap"
            className={styles.input}
            maxLength={VARIANT_CUSTOMISATION_TEXT_MAX}
            value={cForm.name}
            onChange={(e) => { markNestedDraftDirty(); setCForm({ ...cForm, name: e.target.value }); }}
          />
        </div>
        <div className={styles.custFieldRequired}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>
            <Tooltip title="If required, the customer must provide this customisation before adding to cart">Required</Tooltip>
          </div>
          <Switch checked={cForm.isRequired !== false}
            onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, isRequired: val }); }} />
        </div>
      </div>

      {/* Row 2: Type */}
      <div className={styles.subFormRow}>
        <div className={styles.custFieldGrow}>
          <LabelWithInfo
            label={PRODUCT_BUILDER_LABELS.customisationTypeLabel}
            hint={PRODUCT_BUILDER_LABELS.customisationTypeHint}
          />
          <Select
            className={`${styles.select} ${styles.selectFull}`}
            value={cForm.type}
            popupMatchSelectWidth
            optionLabelProp="label"
            onChange={(val) => {
              markNestedDraftDirty();
              setCForm({
                ...cForm,
                type: val,
                DropdownOptions: val === "dropdown" ? cForm.DropdownOptions : [],
                acceptedFileTypes: val === "file" ? (cForm.acceptedFileTypes || []) : [],
                maxFiles: val === "file" ? (cForm.maxFiles || 1) : 1,
                calculatePricePerFile: val === "file" ? cForm.calculatePricePerFile === true : false,
              });
            }}
          >
            <Select.Option value="boolean" label="Yes / No">Yes / No</Select.Option>
            <Select.Option value="text" label="Text">Text</Select.Option>
            <Select.Option value="file" label="File upload">File upload</Select.Option>
            <Select.Option value="dropdown" label="Dropdown">Dropdown</Select.Option>
          </Select>
        </div>
      </div>

      {/* Row 3: File-related fields (equal spacing) */}
      {cForm.type === "file" && (
        <div className={`${styles.subFormRow} ${styles.custFileRow}`}>
          <div className={styles.custFieldEqual}>
            <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Accepted file types</div>
            <Select
              className={`${styles.select} ${styles.selectFull}`}
              mode="multiple"
              value={cForm.acceptedFileTypes || []}
              onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, acceptedFileTypes: val }); }}
              placeholder="Select file types"
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 240 }}
              maxTagCount="responsive"
              optionLabelProp="label"
              showSearch={false}
            >
              <Select.Option value=".jpg" label="JPEG (.jpg)">JPEG (.jpg)</Select.Option>
              <Select.Option value=".jpeg" label="JPEG (.jpeg)">JPEG (.jpeg)</Select.Option>
              <Select.Option value=".png" label="PNG (.png)">PNG (.png)</Select.Option>
              <Select.Option value=".pdf" label="PDF (.pdf)">PDF (.pdf)</Select.Option>
              <Select.Option value=".docx" label="Word (.docx)">Word (.docx)</Select.Option>
              <Select.Option value=".xlsx" label="Excel (.xlsx)">Excel (.xlsx)</Select.Option>
            </Select>
          </div>
          <div className={styles.custFieldEqual}>
            <LabelWithInfo
              label={PRODUCT_BUILDER_LABELS.maxFilesLabel}
              hint={PRODUCT_BUILDER_LABELS.maxFilesHint}
            />
            <Select
              className={`${styles.select} ${styles.selectFull}`}
              value={Math.min(Math.max(Number(cForm.maxFiles) || 1, 1), 5)}
              onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, maxFiles: val }); }}
            >
              {[1, 2, 3, 4, 5].map((count) => (
                <Select.Option key={count} value={count}>{count}</Select.Option>
              ))}
            </Select>
          </div>
          <div className={styles.custFieldEqual}>
            <LabelWithInfo
              label={PRODUCT_BUILDER_LABELS.calculatePricePerFileLabel}
              hint={PRODUCT_BUILDER_LABELS.calculatePricePerFileHint}
            />
            <div className={styles.custToggleAlign}>
              <Switch checked={cForm.calculatePricePerFile === true}
                onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, calculatePricePerFile: val }); }} />
            </div>
          </div>
        </div>
      )}

      {/* Row 4: Price + Add-on */}
      <div className={styles.subFormRow}>
        <div className={styles.custFieldEqual}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Price</div>
          <InputNumber placeholder="e.g. 50" min={0} className={styles.input} style={{ width: "100%" }} value={cForm.price === "" ? null : cForm.price}
            onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, price: val === null || val === undefined ? "" : Number(val) }); }} />
        </div>
        <div className={styles.custFieldEqual}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Add-on price</div>
          <div className={styles.custToggleAlign}>
            <Switch checked={cForm.addonPrice} checkedChildren="Yes" unCheckedChildren="No"
              onChange={(val) => { markNestedDraftDirty(); setCForm({ ...cForm, addonPrice: val }); }} />
          </div>
        </div>
      </div>

      {cForm.type === "dropdown" && (
        <div style={{ marginTop: 10 }}>
          <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Dropdown choices</div>
          <div className={styles.optTable}>
            <div className={styles.optTableHeader}>
              <span className={styles.optColLabel}>Label</span>
              <span className={styles.optColAction}></span>
            </div>
            {cForm.DropdownOptions.map((opt, idx) => (
              <div key={opt.optionId} className={styles.optTableRow}>
                <span className={styles.optColLabel}>
                  <Input
                    placeholder="Choice label"
                    className={styles.input}
                    maxLength={VARIANT_CUSTOMISATION_TEXT_MAX}
                    value={opt.label}
                    onChange={(e) => updateDropdownChoice(idx, e.target.value)}
                  />
                </span>
                <span className={styles.optColAction}>
                  <span className={styles.optDel} onClick={() => removeDropdownChoice(idx)}><X size={12} /></span>
                </span>
              </div>
            ))}
            <div className={styles.optAddRow}>
              <span className={styles.optAddBtn} onClick={addDropdownChoice}>＋ Add choice</span>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const innerModal = (
    <div className={inline ? `${styles.modal} ${styles.modalInline}` : styles.modal}>
        {/* ── Top bar ── */}
        <div className={styles.topBar}>
          <div className={styles.topBarInner}>
            <div className={styles.closeBtn} onClick={onCancel}><X size={12} /></div>
          </div>
        </div>

        <div className={styles.layout}>
          {/* ── Main ── */}
          <div className={styles.main}>
            <div className={styles.productHeader}>
              <div className={styles.productImage}>
                <span className={styles.productImageEmoji}>{isService ? "S" : "P"}</span>
              </div>
              <div className={styles.productHeaderInfo}>
                <div className={styles.productHeaderTitle}>{isService ? "Create Service" : "Create Product"}</div>
                <div className={styles.productHeaderSub}>{isService ? "Fill in the details below to add a new service to your catalog." : "Fill in the details below to add a new product to your catalog."}</div>
              </div>
            </div>

            <Form form={form} layout="vertical" size="small" className={styles.form} onValuesChange={(_, allValues) => { markDirty(); emitDraftChange(allValues); }}>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDot} />
                  {isService ? "Service Overview" : "Product Overview"}
                </div>
                <div className={styles.cardBody}>
                  <Form.Item name="ItemId" hidden><Input /></Form.Item>
                  <Form.Item name="ItemName" label={<span className={styles.fieldLabel}>Name</span>}
                    rules={[{ required: true, message: "Enter name" }]}
                  >
                    <Input placeholder="e.g. Haircut, Massage, Bread" className={styles.input} data-testid="catalog-item-name-input" />
                  </Form.Item>

                  <Form.Item name="Description" label={<span className={styles.fieldLabel}>Description</span>}
                    rules={[{ max: 500, message: "Description must be 500 characters or less" }]}
                  >
                    <TextArea placeholder="Add a short description (max 500 characters)" rows={3} maxLength={500} className={styles.textarea} />
                  </Form.Item>
                  <Form.Item shouldUpdate={(prev, cur) => prev.Description !== cur.Description} style={{ marginBottom: 0, marginTop: -6 }}>
                    {({ getFieldValue }) => {
                      const len = (getFieldValue("Description") || "").length;
                      return <span className={styles.charCount}>{len} / 500</span>;
                    }}
                  </Form.Item>

                  <div className={styles.itemFlagGrid}>
                    <Form.Item name="ItemAvailable" valuePropName="checked" className={styles.itemFlagField}>
                      <Switch />
                    </Form.Item>
                    <div className={styles.itemFlagCopy}>
                      <div className={styles.itemFlagTitle}>{PRODUCT_BUILDER_LABELS.itemAvailableTitle}</div>
                      <div className={styles.itemFlagHint}>{PRODUCT_BUILDER_LABELS.itemAvailableHint}</div>
                    </div>
                    <Form.Item name="RequiresPrebooking" valuePropName="checked" className={styles.itemFlagField}>
                      <Switch disabled={prebookingDisabled} />
                    </Form.Item>
                    <div className={styles.itemFlagCopy}>
                      <div className={styles.itemFlagTitle}>{PRODUCT_BUILDER_LABELS.requiresPrebookingTitle}</div>
                      <div className={styles.itemFlagHint}>{prebookingDisabled ? PRODUCT_BUILDER_LABELS.requiresPrebookingDisabledHint : PRODUCT_BUILDER_LABELS.requiresPrebookingHint}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDot} />
                  Item images
                </div>
                <div className={styles.cardBody}>
                  {(() => {
                    const TILE = 56;
                    const allImages = [
                      ...existingImages.map((img, i) => ({ type: "existing", img, i, url: imageDisplayUrl(img) })),
                      ...selectedFiles.map((file, i) => ({ type: "staged", file, i, url: file._blobUrl || (file._blobUrl = URL.createObjectURL(file)) })),
                    ];
                    const canAddMore = allImages.length < MAX_IMAGES;
                    return (
                      <div style={{ marginTop: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {allImages.map((item) => (
                            <div key={item.type === "existing" ? `e${item.i}` : `f${item.i}`}
                              style={{ position: "relative", width: TILE, height: TILE, borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", flexShrink: 0, background: "#f8fafc" }}>
                              <img src={item.url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", background: "#f8fafc" }} />
                              <button type="button"
                                onClick={() => {
                                  if (item.type === "existing") {
                                    setExistingImages(prev => {
                                      const next = prev.filter((_, j) => j !== item.i);
                                      emitDraftChange(null, { existingImages: next });
                                      return next;
                                    });
                                  } else {
                                    setSelectedFiles(prev => {
                                      const next = prev.filter((_, j) => j !== item.i);
                                      emitDraftChange(null, { selectedFiles: next });
                                      return next;
                                    });
                                  }
                                  markDirty();
                                }}
                                style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, padding: 0, fontSize: 11, lineHeight: 1, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer" }}>
                                {"×"}
                              </button>
                            </div>
                          ))}

                          {/* Upload button — stages file for upload on save */}
                          <Tooltip title={!canAddMore ? `Max ${MAX_IMAGES} images reached` : "Stage image for upload (JPG, PNG, WebP · max 5MB)"}>
                            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", width: TILE, height: TILE, flexShrink: 0, border: "1px dashed #cbd5e1", borderRadius: 8, background: !canAddMore ? "#f1f5f9" : "#fafafa", cursor: !canAddMore ? "not-allowed" : "pointer", color: !canAddMore ? "#cbd5e1" : "var(--zh-brand-primary)", transition: "background 0.15s" }}>
                              <input ref={fileInputRef} type="file" accept={IMAGE_UPLOAD_ACCEPT} multiple onChange={handleFileChange} disabled={!canAddMore} style={{ display: "none" }} data-testid="catalog-item-image-input" />
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                              </svg>
                            </label>
                          </Tooltip>

                          {/* Gallery button */}
                          {hubId && bizId && token && (
                            <Tooltip title={!canAddMore ? `Max ${MAX_IMAGES} images reached` : galleryEmpty ? "No gallery images yet — upload one first" : "Pick from gallery"}>
                              <button type="button"
                                disabled={!canAddMore || galleryEmpty}
                                onClick={() => { if (canAddMore && !galleryEmpty) { setPickSelectedKeys([]); setGalleryOpen(true); refreshGalleryManifest(); } }}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: TILE, height: TILE, flexShrink: 0, border: "1px solid #e2e8f0", borderRadius: 8, background: (!canAddMore || galleryEmpty) ? "#f1f5f9" : "#fafafa", cursor: (!canAddMore || galleryEmpty) ? "not-allowed" : "pointer", color: (!canAddMore || galleryEmpty) ? "#cbd5e1" : "#475569", padding: 0, transition: "background 0.15s" }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              </button>
                            </Tooltip>
                          )}
                        </div>
                        {selectedFiles.length > 0 && (
                          <div style={{ marginTop: 8, fontSize: 12, color: "#ad4e00", background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "6px 8px" }}>
                            Save this item to upload staged images before switching items.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {hubId && bizId && token && (
                <>
                  <Modal title="Pick from gallery" open={galleryOpen} onCancel={() => { setGalleryOpen(false); setPickSelectedKeys([]); }} onOk={applyPickSelection} okText={`Add (${pickSelectedKeys.length})`} okButtonProps={{ disabled: pickSelectedKeys.length === 0 }} width="min(100%,520px)" destroyOnClose>
                    {galleryLoading ? (
                      <div style={{ textAlign:"center", padding:40 }}><Spin /></div>
                    ) : pickableGalleryEntries.length === 0 ? (
                      <div style={{ color:"#888", fontSize:13, marginBottom:12 }}>No images in the shared gallery for this list yet.</div>
                    ) : (
                      <Row gutter={[8,8]} style={{ maxHeight:400, overflowY:"auto" }}>
                        {pickableGalleryEntries.map((entry) => {
                          const basename = basenameFromStorageKey(entry.imageKey) || entry.imageKey;
                          const onItem = existingImages.some((img) => (basenameFromStorageKey(img.imageKey) || img.imageKey) === basename);
                          const selected = pickSelectedKeys.includes(basename);
                          return (
                            <Col xs={8} sm={6} key={basename}>
                              <button type="button" disabled={onItem} onClick={() => { if (!onItem) togglePickKey(entry); }} style={{ border: selected ? "2px solid #1890ff" : "1px solid #d9d9d9", borderRadius:8, padding:0, overflow:"hidden", background: onItem ? "#f5f5f5" : "#fafafa", cursor: onItem ? "not-allowed" : "pointer", width:"100%", aspectRatio:"1", opacity: onItem ? 0.55 : 1 }}>
                                <img src={cdnUrl({ entity: "item", fileType: "media", hubId, bizId, fileName: basename })} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                              </button>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </Modal>
                </>
              )}

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDot} />
                  {isService ? "Pricing" : "Pricing & Inventory"}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.pricingRow}>
                    <Form.Item name="BasePrice" label={<span className={styles.fieldLabel}>Base price</span>}
                      rules={[{ required: true, message: "Enter base price" }]} className={styles.pricingField}
                    >
                      <InputNumber placeholder="e.g. 300" min={0} className={styles.input} style={{ width: "100%" }} data-testid="catalog-item-price-input" />
                    </Form.Item>
                  </div>
                  <div className={styles.pricingRow}>
                    <Form.Item name="Discount" label={<span className={styles.fieldLabel}>Discount (%)</span>} className={styles.pricingField}>
                      <InputNumber placeholder="e.g. 10" min={0} max={100} className={styles.input} style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                </div>
              </div>

              {/* ── Video URLs ── */}
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDot} />
                  <LabelWithInfo
                    as="span"
                    variant="header"
                    label={PRODUCT_BUILDER_LABELS.videoUrlsLabel}
                    hint={PRODUCT_BUILDER_LABELS.videoUrlsHint}
                  />
                </div>
                <div className={styles.cardBody}>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>
                    {PRODUCT_BUILDER_LABELS.videoUrlsHelp}
                  </div>
                  <Form.List name="VideoUrls">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <div key={key} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                            <Form.Item {...restField} name={name} style={{ marginBottom: 0, flex: 1 }}>
                              <Input placeholder="e.g. https://youtube.com/watch?v=..." className={styles.input} />
                            </Form.Item>
                            <Button type="text" danger onClick={() => remove(name)} style={{ flexShrink: 0, minWidth: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <X size={14} />
                            </Button>
                          </div>
                        ))}
                        {fields.length < 3 && (
                          <div className={styles.addRowNew}>
                            <span className={styles.addBtnNew} onClick={() => add("")}>＋ Add video URL</span>
                          </div>
                        )}
                      </>
                    )}
                  </Form.List>
                </div>
              </div>

              {/* ── Variants Accordion ── */}
              <div className={styles.card}>
                <div className={styles.accordionHeader} onClick={() => setActiveAccordion(activeAccordion === 'variants' ? null : 'variants')}>
                  <div className={styles.accordionHeaderLeft}>
                    <div className={styles.accordionTitleRow}>
                      <span className={styles.cardDot} />
                      <LabelWithInfo
                        variant="header"
                        label="Variants"
                        hint={PRODUCT_BUILDER_LABELS.variantsSectionHint}
                        count={savedVariants.length}
                        stopPropagation
                      />
                    </div>
                    <div className={styles.accordionHeaderText}>
                      <div className={styles.accordionSub}>Create size, color, weight or other options.</div>
                    </div>
                  </div>
                  <svg className={`${styles.chevron} ${activeAccordion === 'variants' ? styles.chevronOpen : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={`${styles.accordionContent} ${activeAccordion === 'variants' ? styles.accordionOpen : ''}`}>
                  <div className={styles.cardBody}>
                    {savedVariants.length > 0 && (
                      <div className={styles.savedList}>
                        {savedVariants.map((v, i) => {
                          const isEditingThis = editVariantIdx === i;
                          if (isEditingThis) {
                            return (
                              <div key={i} className={styles.savedItemCard}>
                                  {renderVariantHeaderFields()}
                                <div className={styles.optTable}>
                                  <div className={styles.optTableHeader}>
                                    <span className={styles.optColLabel}>Label</span>
                                    <span className={styles.optColPrice}>Price</span>
                                    <span className={styles.optColAddon}>Add-on <Tooltip title="When enabled, the option price is added to the base item price for the customer"><Info size={12} /></Tooltip></span>
                                    <span className={styles.optColDefault}>Default <Tooltip title="Option auto-selected for the customer when no choice is made"><Info size={12} /></Tooltip></span>
                                    <span className={styles.optColAction}></span>
                                  </div>
                                  {vForm.Options.map((opt, idx) => (
                                    <div key={opt.optionId} className={styles.optTableRow}>
                                      <span className={styles.optColLabel}>
                                        <Input
                                          placeholder="e.g. Large"
                                          className={styles.input}
                                          maxLength={VARIANT_CUSTOMISATION_TEXT_MAX}
                                          value={opt.label}
                                          onChange={(e) => updateVariantOption(idx, "label", e.target.value)}
                                        />
                                      </span>
                                      <span className={styles.optColPrice}>
                                        <Input type="number" placeholder="e.g. 100" className={`${styles.input} ${optionErrors[idx] ? styles.inputError : ""}`} value={opt.price}
                                          onChange={(e) => updateVariantOption(idx, "price", e.target.value === "" ? "" : Number(e.target.value))} />
                                        {optionErrors[idx] && <div className={styles.fieldError}>{optionErrors[idx]}</div>}
                                      </span>
                                      <span className={styles.optColAddon}>
                                        <Switch checked={opt.addonPrice} checkedChildren="Yes" unCheckedChildren="No"
                                          onChange={(val) => updateVariantOption(idx, "addonPrice", val)} />
                                      </span>
                                      <span className={styles.optColDefault}>
                                        <Switch checked={opt.isDefault === true} size="small"
                                          onChange={(val) => updateVariantOption(idx, "isDefault", val)} />
                                      </span>
                                      <span className={styles.optColAction}>
                                        <span className={styles.optDel} onClick={() => removeVariantOption(idx)}><X size={12} /></span>
                                      </span>
                                    </div>
                                  ))}
                                  <div className={styles.optAddRow}>
                                    <span className={styles.optAddBtn} onClick={addVariantOption}>＋ Add option</span>
                                  </div>
                                </div>
                                <div className={styles.subFormActions}>
                                  <Button className={styles.cancelBtn} onClick={(e) => { e.stopPropagation(); setEditVariantIdx(null); setVForm({ name: "", type: "single", isRequired: true, Options: [] }); setOptionErrors({}); }}>Cancel</Button>
                                  <Button className={styles.saveVariantBtn} onClick={(e) => {
                                    e.stopPropagation();
                                    if (!vForm.name) { message.warning("Enter variant name"); return; }
                                    const errors = {};
                                    let hasError = false;
                                    vForm.Options.forEach((o, oi) => {
                                      if (!o.label) return;
                                      if (o.addonPrice === false) {
                                        const p = Number(o.price);
                                        if (p < 1 || isNaN(p)) {
                                          errors[oi] = "Flat price must be at least 1"; hasError = true;
                                        }
                                      }
                                    });
                                    setOptionErrors(errors);
                                    if (hasError) return;
                                    const normalized = { ...vForm, Options: vForm.Options.map((o) => ({ ...o, price: o.price === "" ? 0 : Number(o.price) })) };
                                    const updated = [...savedVariants];
                                    updated[i] = { ...normalized, variantId: v.variantId };
                                    setSavedVariants(updated);
                                    emitDraftChange(null, { savedVariants: updated });
                                    markDirty();
                                    setVForm({ name: "", type: "single", isRequired: true, Options: [] });
                                    setOptionErrors({});
                                    setEditVariantIdx(null);
                                  }}>Update Variant</Button>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={i} className={styles.savedItemCard}>
                              <div className={styles.savedItemHeader}>
                                <div>
                                  <span className={styles.savedItemTitle}>{v.name}</span>
                                  <span className={styles.savedItemMeta}>{v.type === "single" ? "Single choice" : "Multiple choice"}</span>
                                  <span className={styles.savedItemMeta}>{v.isRequired !== false ? "Required" : "Optional"}</span>
                                </div>
                                <div className={styles.savedItemActions}>
                                  <Tooltip title={PRODUCT_BUILDER_LABELS.editVariantTooltip}>
                                    <span className={styles.savedItemIconBtn} onClick={(e) => { e.stopPropagation(); setVForm({ name: v.name, type: v.type, isRequired: v.isRequired !== false, Options: (v.Options || []).map(option => ({ ...option })) }); setEditVariantIdx(i); setShowVariantForm(false); }}><Pencil size={14} /></span>
                                  </Tooltip>
                                  <Tooltip title={PRODUCT_BUILDER_LABELS.deleteVariantTooltip}>
                                    <span className={`${styles.savedItemIconBtn} ${styles.savedItemIconBtnDanger}`} onClick={(e) => { e.stopPropagation(); const updated = savedVariants.filter((_, j) => j !== i); setSavedVariants(updated); emitDraftChange(null, { savedVariants: updated }); markDirty(); }}><Trash2 size={14} /></span>
                                  </Tooltip>
                                </div>
                              </div>
                              {(v.Options || []).length > 0 && (
                                  <div className={styles.savedItemGrid}>
                                    <div className={styles.savedItemGridHeader}>
                                      <span>Label</span>
                                      <span>Price</span>
                                      <span>Add-on <Tooltip title="When enabled, the option price is added to the base item price for the customer"><Info size={12} /></Tooltip></span>
                                      <span>Default <Tooltip title="Option auto-selected for the customer when no choice is made"><Info size={12} /></Tooltip></span>
                                      <span></span>
                                    </div>
                                  {(v.Options || []).map((opt, oi) => (
                                    <div key={opt.optionId || oi} className={styles.savedItemGridRow}>
                                      <span>{opt.label}</span>
                                      <span>₹{opt.price}</span>
                                      <span>{opt.addonPrice ? "Yes" : "No"}</span>
                                      <span>{opt.isDefault ? "✓" : ""}</span>
                                      <span></span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {showVariantForm ? (
                      <div className={styles.subForm}>
                        {renderVariantHeaderFields()}
                        <div className={styles.fieldLabel} style={{ margin: "10px 0 6px" }}>Options</div>
                        <div className={styles.optTable}>
                          <div className={styles.optTableHeader}>
                            <span className={styles.optColLabel}>Label</span>
                            <span className={styles.optColPrice}>Price</span>
                            <span className={styles.optColAddon}>Add-on <Tooltip title="When enabled, the option price is added to the base item price for the customer"><Info size={12} /></Tooltip></span>
                            <span className={styles.optColDefault}>Default <Tooltip title="Option auto-selected for the customer when no choice is made"><Info size={12} /></Tooltip></span>
                            <span className={styles.optColAction}></span>
                          </div>
                          {vForm.Options.map((opt, idx) => (
                            <div key={opt.optionId} className={styles.optTableRow}>
                              <span className={styles.optColLabel}>
                                <Input
                                  placeholder="e.g. Large"
                                  className={styles.input}
                                  maxLength={VARIANT_CUSTOMISATION_TEXT_MAX}
                                  value={opt.label}
                                  onChange={(e) => updateVariantOption(idx, "label", e.target.value)}
                                />
                              </span>
                              <span className={styles.optColPrice}>
                                <Input type="number" placeholder="e.g. 100" className={`${styles.input} ${optionErrors[idx] ? styles.inputError : ""}`} value={opt.price}
                                  onChange={(e) => updateVariantOption(idx, "price", e.target.value === "" ? "" : Number(e.target.value))} />
                                {optionErrors[idx] && <div className={styles.fieldError}>{optionErrors[idx]}</div>}
                              </span>
                              <span className={styles.optColAddon}>
                                <Switch checked={opt.addonPrice} checkedChildren="Yes" unCheckedChildren="No"
                                  onChange={(val) => updateVariantOption(idx, "addonPrice", val)} />
                              </span>
                              <span className={styles.optColDefault}>
                                <Switch checked={opt.isDefault === true} size="small"
                                  onChange={(val) => updateVariantOption(idx, "isDefault", val)} />
                              </span>
                              <span className={styles.optColAction}>
                                <span className={styles.optDel} onClick={() => removeVariantOption(idx)}><X size={12} /></span>
                              </span>
                            </div>
                          ))}
                          <div className={styles.optAddRow}>
                            <span className={styles.optAddBtn} onClick={addVariantOption}>＋ Add option</span>
                          </div>
                        </div>
                        <div className={styles.subFormActions}>
                          <Button className={styles.cancelBtn} onClick={() => { setShowVariantForm(false); setVForm({ name: "", type: "single", isRequired: true, Options: [] }); setOptionErrors({}); setEditVariantIdx(null); }}>Cancel</Button>
                          <Button className={styles.saveVariantBtn} onClick={() => {
                            if (!vForm.name) { message.warning("Enter variant name"); return; }
                            const errors = {};
                            let hasError = false;
                            vForm.Options.forEach((o, i) => {
                              if (!o.label) return;
                              if (o.addonPrice === false) {
                                const p = Number(o.price);
                                if (p < 1 || isNaN(p)) {
                                  errors[i] = "Flat price must be at least 1";
                                  hasError = true;
                                }
                              }
                            });
                            setOptionErrors(errors);
                            if (hasError) return;
                            const normalized = {
                              ...vForm,
                              Options: vForm.Options.map((o) => ({
                                ...o,
                                price: o.price === "" ? 0 : Number(o.price),
                              })),
                            };
                            if (editVariantIdx != null) {
                              const updated = [...savedVariants];
                              updated[editVariantIdx] = { ...normalized, variantId: savedVariants[editVariantIdx].variantId };
                              setSavedVariants(updated);
                              emitDraftChange(null, { savedVariants: updated });
                            } else {
                              const updated = [...savedVariants, { ...normalized, variantId: uid() }];
                              setSavedVariants(updated);
                              emitDraftChange(null, { savedVariants: updated });
                            }
                            markDirty();
                            setVForm({ name: "", type: "single", isRequired: true, Options: [] });
                            setOptionErrors({});
                            setShowVariantForm(false);
                            setEditVariantIdx(null);
                          }}>{editVariantIdx != null ? "Update Variant" : "Save Variant"}</Button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.addRowNew} onClick={() => { setEditVariantIdx(null); setVForm({ name: "", type: "single", isRequired: true, Options: [] }); setShowVariantForm(true); }}>
                        <span className={styles.addBtnNew}>＋ Add Variant</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Customisations Accordion ── */}
              <div className={styles.card}>
                <div className={styles.accordionHeader} onClick={() => setActiveAccordion(activeAccordion === 'customisations' ? null : 'customisations')}>
                  <div className={styles.accordionHeaderLeft}>
                    <div className={styles.accordionTitleRow}>
                      <span className={styles.cardDotCyan} />
                      <LabelWithInfo
                        variant="header"
                        label="Customisations"
                        hint={PRODUCT_BUILDER_LABELS.customisationsSectionHint}
                        count={savedCustomisations.length}
                        stopPropagation
                      />
                    </div>
                    <div className={styles.accordionHeaderText}>
                      <div className={styles.accordionSub}>Add optional extras like gift wrap, engraving, etc.</div>
                    </div>
                  </div>
                  <svg className={`${styles.chevron} ${activeAccordion === 'customisations' ? styles.chevronOpen : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={`${styles.accordionContent} ${activeAccordion === 'customisations' ? styles.accordionOpen : ''}`}>
                  <div className={styles.cardBody}>
                    {savedCustomisations.length > 0 && (
                      <div className={styles.savedList}>
                        {savedCustomisations.map((customisation, index) => {
                          const isEditingThis = editCustIdx === index;
                          if (isEditingThis) {
                            return (
                              <div key={index} className={styles.savedItemCard}>
                                {renderCustomisationFields()}
                                <div className={styles.subFormActions}>
                                  <Button className={styles.cancelBtn} onClick={(e) => { e.stopPropagation(); setEditCustIdx(null); setCForm(defaultCustomisationForm()); }}>Cancel</Button>
                                  <Button className={styles.saveVariantBtn} onClick={(e) => { e.stopPropagation(); commitCustomisationForm(index); }}>Update Customisation</Button>
                                </div>
                              </div>
                            );
                          }
                          return (
                          <div key={index} className={styles.savedItemCard}>
                            <div className={styles.savedItemHeader}>
                              <div>
                                <span className={styles.savedItemTitle}>{customisation.name}</span>
                                <span className={styles.savedItemMeta}>
                                  {customisation.type === "boolean" ? "Yes / No" : customisation.type === "text" ? "Text" : customisation.type === "file" ? "File upload" : "Dropdown"}
                                  {customisation.price > 0 ? ` · ₹${customisation.price}` : ""}
                                </span>
                                <span className={styles.savedItemMeta}>{customisation.isRequired !== false ? "Required" : "Optional"}</span>
                              </div>
                              <div className={styles.savedItemActions}>
                                <Tooltip title={PRODUCT_BUILDER_LABELS.editCustomisationTooltip}>
                                  <span className={styles.savedItemIconBtn} onClick={(e) => { e.stopPropagation(); setCForm(customisationToForm(customisation)); setEditCustIdx(index); setShowCustForm(false); }}><Pencil size={14} /></span>
                                </Tooltip>
                                <Tooltip title={PRODUCT_BUILDER_LABELS.deleteCustomisationTooltip}>
                                  <span className={`${styles.savedItemIconBtn} ${styles.savedItemIconBtnDanger}`} onClick={(e) => { e.stopPropagation(); const updated = savedCustomisations.filter((_, j) => j !== index); setSavedCustomisations(updated); emitDraftChange(null, { savedCustomisations: updated }); markDirty(); }}><Trash2 size={14} /></span>
                                </Tooltip>
                              </div>
                            </div>
                            {customisation.type === "dropdown" && customisation.DropdownOptions?.length > 0 && (
                              <div className={`${styles.savedItemGrid} ${styles.savedItemGridStack}`}>
                                <div className={styles.savedItemGridHeader}>
                                  <span>Choices</span>
                                  <span></span>
                                  <span></span>
                                  <span></span>
                                </div>
                                {customisation.DropdownOptions.map((opt, optionIndex) => (
                                  <div key={opt.optionId || optionIndex} className={styles.savedItemGridRow}>
                                    <span>{opt.label}</span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {customisation.type === "file" && (
                              <div className={`${styles.savedItemGrid} ${styles.savedItemGridStack}`}>
                                {customisation.acceptedFileTypes?.length > 0 && (
                                  <div className={styles.savedItemGridRow}>
                                    <span className={styles.savedItemMetaInline}>Accepted: {customisation.acceptedFileTypes.join(", ")}</span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                  </div>
                                )}
                                <div className={styles.savedItemGridRow}>
                                  <span className={styles.savedItemMetaInline}>Max files: {Math.min(Math.max(Number(customisation.maxFiles) || 1, 1), 5)}</span>
                                  <span></span>
                                  <span className={styles.savedItemMetaInline}>Per-file pricing: {customisation.calculatePricePerFile || customisation.calculatePricePerPage ? "Yes" : "No"}</span>
                                  <span></span>
                                </div>
                              </div>
                            )}
                            {customisation.type !== "dropdown" && customisation.type !== "file" && (
                              <div className={`${styles.savedItemGrid} ${styles.savedItemGridStack}`}>
                                <div className={styles.savedItemGridRow}>
                                  <span className={styles.savedItemMetaInline}>Price: ₹{customisation.price}</span>
                                  <span></span>
                                  <span className={styles.savedItemMetaInline}>Add-on: {customisation.addonPrice ? "Yes" : "No"}</span>
                                  <span></span>
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {showCustForm && editCustIdx == null ? (
                      <div className={styles.subForm}>
                        {renderCustomisationFields()}
                        <div className={styles.subFormActions}>
                          <Button className={styles.cancelBtn} onClick={() => { setShowCustForm(false); setCForm(defaultCustomisationForm()); setEditCustIdx(null); }}>Cancel</Button>
                          <Button className={styles.saveVariantBtn} onClick={() => commitCustomisationForm(null)}>Save Customisation</Button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.addRowNew} onClick={() => { setEditCustIdx(null); setCForm(defaultCustomisationForm()); setShowCustForm(true); }}>
                        <span className={styles.addBtnNew}>＋ Add Customisation</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          </div>

        </div>

        {/* ── Bottom bar ── */}
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarInner}>
            {showSaveButton && (
              <div className={styles.bottomBarRight}>
                <Button type="primary" className={styles.continueBtn} onClick={handleSave} loading={saving} disabled={saving}>{saving ? "Saving..." : "Save & Continue"}</Button>
              </div>
            )}
          </div>
        </div>
      </div>
  );

  if (inline) {
    return <div className={styles.inlineRoot}>{innerModal}</div>;
  }
  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={onCancel} />
      {innerModal}
    </div>
  );
});

export default ProductBuilder;
